import React, { useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
  TextField,
  Typography,
  // useMediaQuery,
  useTheme,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { Link } from "react-router-dom";
import { adminItemColumns } from "../../components/admin/AdminItemColumns";
import { fetchWithAuth } from '../../utils/api';

const AdminEditItems = () => {
  const [searchInput, setSearchInput] = useState("");
  const [rows, setRows] = useState([]);
  const [changes, setChanges] = useState({});
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [successSnackbarOpen, setSuccessSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [originalRows, setOriginalRows] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  const theme = useTheme();
  // const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handleSearch = async () => {
    if (isSearching || !searchInput.trim()) return;

    setIsSearching(true);

    const names = searchInput
      .split(",")
      .map((n) => n.trim())
      .filter(Boolean);

    try {
      const data = await fetchWithAuth("/items/details", {
        method: "POST",
        body: { names },
      });

      setRows((prev) => {
        const existingIds = new Set(prev.map((row) => row.id));
        const newRows = data.items.filter((item) => !existingIds.has(item.id));
        if (newRows.length > 0) {
          setOriginalRows((prev) => [...prev, ...newRows]);
        }
        return [...prev, ...newRows];
      });

      if (data.rejected?.length) {
        setSnackbarMessage(`Not found: ${data.rejected.join(", ")}`);
        setSnackbarOpen(true);
      }
      setSearchInput("");
    } catch (err) {
      console.error(err);
      setSnackbarMessage("Failed to fetch item details.");
      setSnackbarOpen(true);
    } finally {
      setIsSearching(false);
    }
  };

  const handleRowUpdate = (newRow, oldRow) => {
    if (!newRow.name) {
      alert("Item name cannot be empty.");
      return oldRow;
    }

    const hasMin = newRow.current_value_min != null;
    const hasMax = newRow.current_value_max != null;

    if (hasMin !== hasMax) {
      return newRow;
    }

    if (hasMin && hasMax) {
      const min = Number(newRow.current_value_min);
      const max = Number(newRow.current_value_max);

      if (
        !Number.isInteger(min) ||
        !Number.isInteger(max) ||
        min <= 0 ||
        max <= 0 ||
        min > max
      ) {
        alert("Invalid value range: min and max must be integers > 0, and min ≤ max.");
        return oldRow;
      }
    }

    const original = originalRows.find((r) => r.id === newRow.id);
    const isChanged = ["name", "current_value_min", "current_value_max", "is_stable", "is_volatile", "type", "notes"]
      .some((field) => newRow[field] !== original?.[field]);

    setChanges((prev) => {
      const updated = { ...prev };
      if (isChanged) {
        updated[newRow.id] = newRow;
      } else {
        delete updated[newRow.id];
      }
      return updated;
    });

    setRows((prev) => prev.map((row) => (row.id === newRow.id ? newRow : row)));
    return newRow;
  };

  const handleConfirmSave = async () => {
    try {
      const updates = Object.values(changes).map((row) => ({
        id: row.id,
        name: row.name,
        current_value_min: row.current_value_min,
        current_value_max: row.current_value_max,
        is_stable: row.is_stable,
        is_volatile: row.is_volatile,
        type: row.type,
        notes: row.notes,
      }));

      await fetchWithAuth("/items/bulk-update", {
        method: "PUT",
        body: { updates },
      });

      setConfirmOpen(false);
      setChanges({});
      setSuccessSnackbarOpen(true);
      setOriginalRows(rows); // update originals for accurate diffing
    } catch (err) {
      console.error(err);
      setSnackbarMessage("Failed to save changes.");
      setSnackbarOpen(true);
    }
  };

  const handleResetChanges = () => {
    setRows(originalRows.map((r) => ({ ...r }))); // reset values only
    setChanges({});
  };

  const handleRemove = (id) => {
    setRows((prev) => prev.filter((row) => row.id !== id));
    setChanges((prev) => {
      const updated = { ...prev };
      delete updated[id];
      return updated;
    });
    setOriginalRows((prev) => prev.filter((row) => row.id !== id));
  };

  // if (isMobile) {
  //   return <Navigate to="/admin/editSingleItem" replace />;
  // }

  return (
    <Box sx={{ px: 2, py: 1 }}>
      <Typography
        variant="h5"
        sx={{
          color: theme.palette.accent.main,
          fontWeight: "bold",
          textAlign: "center",
          mb: 2,
        }}
      >
        admin: edit items
      </Typography>

      <Box
        sx={{
          maxWidth: "1300px",
          mx: "auto",
          p: 2,
          backgroundColor: theme.palette.background.paper,
          borderRadius: 2,
          boxShadow: 3,
        }}
      >
        <Box mb={2}>
          <Button
            component={Link}
            to="/admin/editSingleItem"
            variant="outlined"
            sx={{ textTransform: "none" }}
          >
            Switch to Single Item Editor →
          </Button>
        </Box>

        <Box
          component="form"
          onSubmit={(e) => {
            e.preventDefault();
            handleSearch();
          }}
          display="flex"
          gap={2}
          mb={2}
        >
          <Button
            variant="outlined"
            color="error"
            onClick={() => {
              setRows([]);
              setOriginalRows([]);
              setChanges({});
              setSearchInput("");
            }}
            sx={{ minWidth: 120 }}
          >
            Clear All
          </Button>

          <TextField
            fullWidth
            label="Comma-separated item names"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            size="small"
          />
          
          <Button
            variant="contained"
            type="submit"
            disabled={isSearching}
            sx={{ minWidth: 120 }}
          >
            {isSearching ? "Searching..." : "Search"}
          </Button>
        </Box>

        <Box sx={{ height: 525, width: "100%", mb: 3 }}>
          <DataGrid
            rows={rows}
            rowHeight={51}
            columns={adminItemColumns(changes, originalRows, handleRemove)}
            processRowUpdate={handleRowUpdate}
            disableRowSelectionOnClick
            experimentalFeatures={{ newEditingApi: true }}
            sx={{
              backgroundColor: theme.palette.background.default,
              color: theme.palette.text.primary,
              border: "1px solid",
              borderColor: theme.palette.divider,
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: theme.palette.background.paper,
                color: theme.palette.accent.main,
                fontWeight: "bold",
              },
              "& .edited-cell": {
                backgroundColor: theme.palette.primary.light + "22",
              },
            }}
            initialState={{
              pagination: {
                paginationModel: { pageSize: 8, page: 0 },
              },
            }}
          />
        </Box>

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={4000}
          onClose={() => setSnackbarOpen(false)}
          message={snackbarMessage}
        />
        <Snackbar
          open={successSnackbarOpen}
          autoHideDuration={4000}
          onClose={() => setSuccessSnackbarOpen(false)}
          message="Changes saved successfully!"
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        />

        {rows.length > 0 && Object.keys(changes).length > 0 && (
          <>
            <Box display="flex" gap={2} mt={2}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleResetChanges}
                sx={{ flex: 1 }}
              >
                Reset Changes
              </Button>

              <Button
                variant="contained"
                color="primary"
                onClick={() => setConfirmOpen(true)}
                sx={{ flex: 1 }}
              >
                Save Changes
              </Button>
            </Box>

            <Dialog
              open={confirmOpen}
              onClose={() => setConfirmOpen(false)}
              maxWidth="sm"
              fullWidth
            >
              <DialogTitle>Confirm Changes</DialogTitle>
              <DialogContent dividers>
                <Box maxHeight={300} overflow="auto">
                  {Object.values(changes).map((item) => {
                    const original = originalRows.find((r) => r.id === item.id) || {};
                    return (
                      <Box key={item.id} mb={2}>
                        <Typography variant="subtitle1">
                          {item.name} (ID: {item.id})
                        </Typography>
                        <ul>
                          {Object.entries(item).map(([key, val]) => {
                            if (key === "id") return null;
                            const oldVal = original[key];
                            if (oldVal !== val) {
                              return (
                                <li key={key}>
                                  {key}: <b>{String(oldVal)}</b> → <b>{String(val)}</b>
                                </li>
                              );
                            }
                            return null;
                          })}
                        </ul>
                      </Box>
                    );
                  })}
                </Box>
              </DialogContent>
              <DialogActions>
                <Button onClick={handleConfirmSave} variant="contained">
                  Confirm
                </Button>
                <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
              </DialogActions>
            </Dialog>
          </>
        )}
      </Box>
    </Box>
  );
};

export default AdminEditItems;